import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import Header from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Search, X } from "lucide-react";
import { ShoppingCart } from "@/components/ShoppingCart";
import { CountdownBanner } from "@/components/CountdownBanner";
import { InviteNudgeBanner } from "@/components/InviteNudgeBanner";
import { ListFirstProductBanner } from "@/components/ListFirstProductBanner";
import { CommunityChampions } from "@/components/CommunityChampions";
import { BrowseModeNav, BROWSE_TABS, type BrowseTabId } from "@/components/BrowseModeNav";
import { SwipeTabs, type SwipePanel } from "@/components/SwipeTabs";
import Home from "@/pages/Home";
import HomeFeed from "@/pages/HomeFeed";
import { BrowseSearchResults, type SearchItem, type SearchProducer, type SearchBox } from "@/components/BrowseSearchResults";
import {
  matchesItem,
  matchesAssist,
  matchesProducer,
  matchesBox,
  buildProducerIndex,
  type SearchAssist,
} from "@shared/searchFields";
import { trackEvent, logSearch, logSearchClick } from "@/lib/analytics";
import { BROWSE_FILTERS_EVENT, type BrowseFilters } from "@/lib/browseFilters";
import { useCart } from "@/hooks/useCart";
import { useAuth } from "@/hooks/useAuth";
import { useBuyerLocation } from "@/hooks/useBuyerLocation";
import type { CartItemWithDetails } from "@shared/schema";

type BrowseMode = Exclude<BrowseTabId, "feed">;

function indexFromParams(params: URLSearchParams): number {
  // Legacy: ?feed=0 opened the old tabbed Home, which defaulted to Boxes.
  if (params.get("feed") === "0") return 1;
  const mode = params.get("mode") ?? params.get("browse");
  const found = BROWSE_TABS.findIndex((t) => t.id === mode);
  return found > 0 ? found : 0;
}

/** Owns the shared browse chrome (Header, banners, search, tablist, cart) and
 *  hosts all six browse tabs as swipeable panels. Each panel is a Home locked
 *  to one mode (or the Feed), sharing this component's search / zip / buyer
 *  location so every tab filters and sorts against the same values. This
 *  component is the sole writer of the URL, so switching tabs never remounts. */
export default function BrowseHome() {
  const { user } = useAuth();
  const { cart, updateQuantity, removeFromCart, clearCart, cartCount } = useCart();
  const [, setLocation] = useLocation();
  const search = useSearch();

  const [activeIndex, setActiveIndex] = useState(() =>
    indexFromParams(new URLSearchParams(typeof window !== "undefined" ? window.location.search : "")),
  );
  const [searchQuery, setSearchQuery] = useState(() =>
    new URLSearchParams(typeof window !== "undefined" ? window.location.search : "").get("q") ?? "",
  );
  const [zipCode, setZipCode] = useState(() =>
    new URLSearchParams(typeof window !== "undefined" ? window.location.search : "").get("zip") ?? "",
  );
  const [isCartOpen, setIsCartOpen] = useState(false);

  const { buyerLocation, isLocating, locationError, handleUseCurrentLocation } = useBuyerLocation(zipCode);

  useEffect(() => {
    if (!zipCode && user?.zipCode) setZipCode(user.zipCode);
  }, [user?.zipCode, zipCode]);

  const hasAutoLocatedRef = useRef(false);
  useEffect(() => {
    if (hasAutoLocatedRef.current) return;
    if (buyerLocation?.point) return;
    hasAutoLocatedRef.current = true;
    void handleUseCurrentLocation();
  }, [buyerLocation, handleUseCurrentLocation]);

  // Sole URL writer. replaceState (not wouter) so a tab change never changes
  // wouter's location and never remounts this component or its panels.
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Merge, don't replace: the active panel owns its browse-filter keys in the
    // same query string (the map reads them), so building it from scratch here
    // would wipe them on every tab / search / zip change.
    const params = new URLSearchParams(window.location.search);
    const tab = BROWSE_TABS[activeIndex];
    if (tab && tab.id !== "feed") params.set("mode", tab.id);
    else params.delete("mode");
    if (searchQuery.trim()) params.set("q", searchQuery.trim());
    else params.delete("q");
    if (zipCode.trim()) params.set("zip", zipCode.trim());
    else params.delete("zip");
    // Legacy entry param. Merging (rather than rebuilding) the query string kept
    // it alive, and indexFromParams short-circuits on it, so every tab change
    // was being read straight back as "go to Boxes".
    params.delete("feed");
    const query = params.toString();
    const next = query ? `/?${query}` : "/";
    const current = `${window.location.pathname}${window.location.search}`;
    // Preserve history state so the map's pushed entry survives (MapToggle keys
    // its back button off history.state.stoopMap).
    if (next !== current) window.history.replaceState(window.history.state, "", next);
  }, [activeIndex, searchQuery, zipCode]);

  // Genuine navigations only (deep link, back button). Our own replaceState
  // above doesn't update wouter's useSearch, so this can't feed back into a loop.
  useEffect(() => {
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    const idx = indexFromParams(params);
    setActiveIndex((cur) => (cur === idx ? cur : idx));
    const q = params.get("q") ?? "";
    setSearchQuery((cur) => (cur === q ? cur : q));
  }, [search]);

  // The map broadcasts filter changes (including the search) on this channel.
  // Listen directly rather than relying on a mounted Home panel to relay it, so
  // clearing or setting a search on the map always reaches the search box.
  useEffect(() => {
    const onShared = (event: Event) => {
      const next = (event as CustomEvent<BrowseFilters>).detail;
      if (!next || next.q === undefined) return;
      setSearchQuery((cur) => (cur === next.q ? cur : next.q));
    };
    window.addEventListener(BROWSE_FILTERS_EVENT, onShared);
    return () => window.removeEventListener(BROWSE_FILTERS_EVENT, onShared);
  }, []);

  const goToTabId = (id: BrowseTabId) => {
    const idx = BROWSE_TABS.findIndex((t) => t.id === id);
    if (idx >= 0) setActiveIndex(idx);
  };

  // The feed hands a category over when a chip sends you to Listings. Held here
  // rather than pushed straight at the panel: Listings is two tabs away, so it
  // usually isn't mounted yet at the moment of the tap.
  const [categoryRequest, setCategoryRequest] = useState<{ value: string | null; seq: number }>({
    value: null,
    seq: 0,
  });

  const goToModeFromFeed = (mode: string, extra?: Record<string, string>) => {
    // A chip carries a category; "All" and "See all" carry none and should clear
    // whatever the Listings tab was last filtered to.
    if (mode === "products") {
      setCategoryRequest((prev) => ({ value: extra?.category ?? null, seq: prev.seq + 1 }));
    }
    goToTabId(mode as BrowseTabId);
  };

  const { data: cartItemsDetails = [] } = useQuery<CartItemWithDetails[]>({
    queryKey: ["/api/cart/details", JSON.stringify(cart)],
    queryFn: async () => {
      if (cart.length === 0) return [];
      const res = await fetch(`/api/cart/details?cart=${encodeURIComponent(JSON.stringify(cart))}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: cart.length > 0,
  });

  // Global search: one typeahead over products, makers and boxes at once, so
  // search behaves identically on every tab (the feed, saved and following used
  // to ignore it entirely). Fetched only while searching, and under the same
  // query keys the panels use, so it shares their cache instead of double-loading.
  const trimmedQuery = searchQuery.trim();
  const searchActive = trimmedQuery !== "";
  const [searchFocused, setSearchFocused] = useState(false);
  const showSearchResults = searchFocused && searchActive;

  const { data: searchItems = [], isFetching: itemsFetching } = useQuery<SearchItem[]>({
    queryKey: ["/api/items", "all"],
    queryFn: async () => {
      const res = await fetch("/api/items", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: searchActive,
    staleTime: 60_000,
  });
  const { data: searchProducers = [], isFetching: producersFetching } = useQuery<SearchProducer[]>({
    queryKey: ["/api/sellers"],
    queryFn: async () => {
      const res = await fetch("/api/sellers", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: searchActive,
    staleTime: 60_000,
  });
  const { data: searchBoxes = [], isFetching: boxesFetching } = useQuery<SearchBox[]>({
    queryKey: ["/api/boxes", zipCode, false],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (zipCode.trim()) params.set("zip", zipCode.trim());
      const url = params.toString() ? `/api/boxes?${params.toString()}` : "/api/boxes";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: searchActive,
    staleTime: 60_000,
  });
  const searchLoading = itemsFetching || producersFetching || boxesFetching;

  // AI assist (Phase 2): only when local matching finds nothing, only after the
  // typing settles, and only for a query long enough to mean something. The
  // instant path stays instant and offline-safe; this widens the vocabulary for
  // the long tail ("something for a picnic") the synonym list can't cover.
  const localHasItems = useMemo(
    () => (searchActive ? searchItems.some((i) => matchesItem(trimmedQuery, i)) : true),
    [searchActive, trimmedQuery, searchItems],
  );
  const [assist, setAssist] = useState<SearchAssist | null>(null);
  const [assistLoading, setAssistLoading] = useState(false);
  // Queries whose outcome we've already recorded this session, so retyping or
  // re-focusing the box doesn't inflate the counts.
  const loggedMissRef = useRef<Set<string>>(new Set());

  // Durable search log: every settled query with what it actually returned,
  // plus which result the searcher opened. Reviewable query-by-query for
  // accuracy and the raw material for per-user taste and future ranking.
  const searchCounts = useMemo(() => {
    if (!searchActive) return { items: 0, producers: 0, boxes: 0 };
    const idx = buildProducerIndex(searchItems as any);
    const hit = (i: SearchItem) =>
      matchesItem(trimmedQuery, i as any) || (assist ? matchesAssist(assist, i as any) : false);
    return {
      items: searchItems.reduce((n, i) => n + (hit(i) ? 1 : 0), 0),
      producers: searchProducers.reduce(
        (n, p) => n + (matchesProducer(trimmedQuery, p as any, idx) ? 1 : 0),
        0,
      ),
      boxes: searchBoxes.reduce(
        (n, b) =>
          n +
          (matchesBox(trimmedQuery, b) ||
          (assist?.terms ?? []).some((t) => matchesBox(t, b))
            ? 1
            : 0),
        0,
      ),
    };
  }, [searchActive, trimmedQuery, searchItems, searchProducers, searchBoxes, assist]);

  const loggedSearchRef = useRef<Set<string>>(new Set());
  const searchLogIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!searchActive || trimmedQuery.length < 2) return;
    // Wait for the catalog, and for assist to settle, so the counts we record
    // are what the searcher actually ended up seeing rather than a mid-flight
    // snapshot.
    if (searchItems.length === 0 || assistLoading) return;
    if (loggedSearchRef.current.has(trimmedQuery)) return;

    const timer = setTimeout(() => {
      if (loggedSearchRef.current.has(trimmedQuery)) return;
      loggedSearchRef.current.add(trimmedQuery);
      void logSearch({
        q: trimmedQuery,
        itemCount: searchCounts.items,
        producerCount: searchCounts.producers,
        boxCount: searchCounts.boxes,
        assistUsed: Boolean(assist && (assist.terms.length || assist.categories.length)),
        assistTerms: assist ?? undefined,
      }).then((id) => {
        if (id) searchLogIdRef.current = id;
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [searchActive, trimmedQuery, searchItems, assistLoading, assist, searchCounts]);

  useEffect(() => {
    setAssist(null);
    // searchItems.length guards a cold-start race: before the catalog loads,
    // "nothing matched" is indistinguishable from "nothing exists", and both
    // the assist call and the miss log would be measuring an empty catalog.
    if (!searchActive || localHasItems || trimmedQuery.length < 3) return;
    if (searchItems.length === 0) return;
    // Nothing local matched, so there is nothing to disturb by waiting.
    let cancelled = false;
    const timer = setTimeout(() => {
      setAssistLoading(true);
      fetch("/api/search/assist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ q: trimmedQuery }),
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (cancelled || !data) return;
          const next: SearchAssist = {
            terms: data.terms ?? [],
            dietary: data.dietary ?? [],
            categories: data.categories ?? [],
          };
          setAssist(next);

          // Phase 3 (on-device semantic search) should be built against what
          // people actually type, not what we imagine. Every query that got
          // here already found ZERO items locally, so this is the real gap;
          // `assist` records whether the AI layer rescued it (>0) or the query
          // is still unanswerable (0) — the latter is the hardest set and the
          // one an embedding model has to earn its download on.
          if (loggedMissRef.current.has(trimmedQuery)) return;
          loggedMissRef.current.add(trimmedQuery);
          const rescued = searchItems.reduce(
            (n, item) => n + (matchesAssist(next, item as any) ? 1 : 0),
            0,
          );
          trackEvent("search_miss", "/", {
            q: trimmedQuery.slice(0, 120),
            assist: rescued,
            terms: next.terms.slice(0, 12),
            cats: next.categories,
            diet: next.dietary,
            catalog: searchItems.length,
          });
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelled) setAssistLoading(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      setAssistLoading(false);
    };
  }, [searchActive, localHasItems, trimmedQuery, searchItems]);

  const activeTab = BROWSE_TABS[activeIndex]?.id ?? "feed";
  const searchPlaceholder =
    activeTab === "boxes"
      ? "Search weekly boxes"
      : activeTab === "producers"
        ? "Search growers and makers"
        : "Search local products";

  const modeProps = (mode: BrowseMode) => ({
    embedded: true as const,
    lockedMode: mode,
    // The feed has no filter state of its own, so while it's showing let the
    // listings panel own the shared filter keys — otherwise a filter changed on
    // the map opened from the feed belongs to nobody and is dropped.
    isActive: activeTab === mode || (activeTab === "feed" && mode === "products"),
    searchQuery,
    onSearchQueryChange: setSearchQuery,
    assist,
    zipCode,
    buyerLocation,
    onModeChange: goToTabId,
    categoryRequest,
    // Flush with the pill bar's bottom. The hairline this used to overlap by 1px
    // was the rounding error in the reserved height, not a sub-pixel seam, so the
    // nudge now only makes the row shift as it sticks.
    stickyTop: tablistH,
  });

  const panels: SwipePanel[] = [
    {
      id: "feed",
      label: "Feed",
      hasTab: false,
      render: () => (
        <HomeFeed
          embedded
          buyerLocation={buyerLocation}
          zipCode={zipCode}
          onNavigateMode={goToModeFromFeed}
        />
      ),
    },
    { id: "boxes", label: "Boxes", render: () => <Home {...modeProps("boxes")} /> },
    { id: "products", label: "Listings", render: () => <Home {...modeProps("products")} /> },
    { id: "producers", label: "Growers & Makers", render: () => <Home {...modeProps("producers")} /> },
    { id: "saved", label: "Saved", render: () => <Home {...modeProps("saved")} /> },
    { id: "following", label: "Following", render: () => <Home {...modeProps("following")} /> },
  ];

  // Production is one document scroll: the location row, banners and search
  // scroll away while the logo row and the pill bar stay pinned. Panels scroll
  // independently here, so we reproduce it by overlaying the collapsible chrome
  // on the pager and sliding it up by the active panel's scroll offset. It gets
  // clipped by the pager's overflow, and the pill bar comes to rest directly
  // under the header exactly as `sticky top: 54px` does today.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const headerBlockRef = useRef<HTMLDivElement | null>(null);
  const tablistRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const collapseMaxRef = useRef(0);
  const headerTailHRef = useRef(0);
  const lastScrollTopRef = useRef(0);
  // Measured off the overlay itself rather than summed from its parts: rounding
  // three heights and adding them overshot the overlay's real height by a
  // fraction of a pixel, and the panel reserves this much space, so the first
  // card sat that fraction below the pill bar until the filter row stuck to its
  // absolute offset and the error disappeared.
  const [overlayH, setOverlayH] = useState(0);
  const [tablistH, setTablistH] = useState(0);
  const [headerTailH, setHeaderTailH] = useState(0);

  // Let the compositor drive the collapse off the panel's own scroll where the
  // engine supports it, so the shared chrome and the panel's sticky filter row
  // move on one timeline instead of drifting apart. Ranges are zero until the
  // heights are measured, and a zero-length range would snap the chrome straight
  // to its collapsed state, so hold the class back until then.
  // Test every property the collapse actually uses, not just `animation-timeline`.
  // The chrome is not a descendant of the scroller, so it reaches it through a
  // NAMED timeline (`scroll-timeline-name` on the panel, `timeline-scope` on the
  // shell). WebKit shipped scroll-driven animations without `timeline-scope`, so
  // an iPhone answered yes to `animation-timeline: scroll()`, took this path, and
  // then never resolved `--stoop-active-panel` — the chrome stayed put while the
  // panels reserved space and stuck their filter rows as if it had collapsed, so
  // the banners, the filter row and the first cards all drew on top of each other.
  const supportsScrollTimeline =
    typeof CSS !== "undefined" &&
    CSS.supports?.("animation-timeline: --stoop-probe") === true &&
    CSS.supports?.("scroll-timeline-name: --stoop-probe") === true &&
    CSS.supports?.("timeline-scope: --stoop-probe") === true;
  // Everything above the pill bar retires; the pill bar itself comes to rest.
  const collapseMax = Math.max(0, overlayH - tablistH);
  // Claiming support and actually animating are different things, and the
  // failure mode is the whole surface drawing on top of itself. Verify once on
  // the first real scroll and hand back to the scroll handler if the chrome
  // hasn't moved.
  const [timelineDead, setTimelineDead] = useState(false);
  const timelineCheckedRef = useRef(false);
  const nativeCollapse = Boolean(supportsScrollTimeline) && !timelineDead && collapseMax > 0;

  // Slide the chrome away imperatively, straight on the scroll frame. Routing it
  // through React state re-rendered BrowseHome and every mounted Home panel 60x a
  // second, which is what made the scroll choppy and let the pill bar lag the
  // content into a seam.
  const applyCollapse = useCallback((scrollTop: number) => {
    lastScrollTopRef.current = scrollTop;
    if (nativeCollapse) {
      if (!timelineCheckedRef.current && scrollTop > 40 && collapseMaxRef.current > 40) {
        timelineCheckedRef.current = true;
        const node = overlayRef.current;
        const t = node ? getComputedStyle(node).transform : "none";
        if (t === "none" || t === "matrix(1, 0, 0, 1, 0, 0)") setTimelineDead(true);
      }
      return;
    }
    const top = Math.max(scrollTop, 0);
    // The header tail (Buyer|Sell + location) leads, then the banners and search,
    // exactly the order a single document scroll retires them in on production.
    const tailBy = Math.min(top, headerTailHRef.current);
    rootRef.current?.style.setProperty("--stoop-header-tail-collapse", `${tailBy}px`);
    const by = Math.min(top, collapseMaxRef.current);
    const node = overlayRef.current;
    if (node) node.style.transform = `translate3d(0, ${-by}px, 0)`;
  }, [nativeCollapse]);

  // This page is exactly one viewport tall and scrolls inside its panels, but it
  // sits inside the app-wide bottom padding that clears the nav, so the document
  // itself was ~100px scrollable. Nothing here scrolls it deliberately, which
  // made any stray offset — a restored one, an overscroll chain — strand the
  // header underneath the status bar with no way to scroll back. Pin the
  // document while the pager owns the screen.
  // Drop whatever the scroll handler wrote before the measurements landed, so it
  // can't sit underneath the animation as a stale offset.
  useEffect(() => {
    if (!nativeCollapse) return;
    overlayRef.current?.style.removeProperty("transform");
    rootRef.current?.style.removeProperty("--stoop-header-tail-collapse");
  }, [nativeCollapse]);

  useLayoutEffect(() => {
    const overlay = overlayRef.current;
    const tablist = tablistRef.current;
    const headerBlock = headerBlockRef.current;
    if (!overlay || !tablist || !headerBlock) return;
    const logoRow = headerBlock.querySelector("header");
    const tail = headerBlock.querySelector("[data-header-tail]");
    if (!logoRow || !tail) return;
    const apply = () => {
      // Exact, not rounded: the panel reserves overlayH, so any rounding here
      // reopens the gap between the pill bar and the first row beneath it.
      const oh = overlay.getBoundingClientRect().height;
      const th = tablist.getBoundingClientRect().height;
      setOverlayH(oh);
      setTablistH(th);
      collapseMaxRef.current = Math.max(0, oh - th);
      // Pin the block to the sticky logo row only; the tail overflows into the
      // pager region so it can slide up behind the logo row as you scroll.
      headerBlock.style.height = `${logoRow.getBoundingClientRect().height}px`;
      const tailH = tail.getBoundingClientRect().height;
      headerTailHRef.current = tailH;
      setHeaderTailH(tailH);
      applyCollapse(lastScrollTopRef.current);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(overlay);
    ro.observe(tablist);
    ro.observe(logoRow);
    ro.observe(tail);
    return () => ro.disconnect();
  }, [applyCollapse]);

  return (
    <div
      ref={rootRef}
      // Taken out of flow. This is a full-screen shell that scrolls inside its
      // panels, but in normal flow it still sat above the app's bottom padding
      // and the site footer — 100px of siblings under a page already the height
      // of the viewport. That made the document scrollable, and any offset
      // landing on it (a restored one on Back, a scrollIntoView) slid the header
      // up under the status bar with no way to scroll back. Fixed, the document
      // has nothing to scroll.
      className={`fixed inset-x-0 top-0 flex flex-col overflow-hidden bg-background${nativeCollapse ? " stoop-native-collapse" : ""}`}
      style={
        {
          height: "100dvh",
          // Scoped here so both the header tail and the overlay can follow the
          // panel scroller's timeline even though neither is its descendant.
          timelineScope: "--stoop-active-panel",
          "--stoop-collapse-max": `${collapseMax}px`,
          "--stoop-tail-max": `${headerTailH}px`,
        } as CSSProperties
      }
    >
      <div ref={headerBlockRef} className="relative z-50 shrink-0">
        <Header
          cartCount={cartCount}
          onCartClick={() => setIsCartOpen(true)}
          collapsed={false}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          locationLabel={
            buyerLocation?.source === "browser_gps"
              ? buyerLocation.label || "Near you"
              : zipCode?.trim() || user?.zipCode
                ? `ZIP ${zipCode?.trim() || user?.zipCode}`
                : "Set location"
          }
          onUseCurrentLocation={handleUseCurrentLocation}
          isLocating={isLocating}
          locationError={locationError}
        />

      </div>

      <div className="relative flex-1 overflow-hidden">
        <div ref={overlayRef} className="stoop-collapsible-chrome absolute inset-x-0 top-0 z-40">
          {/* The header tail overflows the pinned block into this region and
              slides with us, so reserve its height here rather than re-render. */}
          <div style={{ height: headerTailH }} />

          <div>
            <CountdownBanner />
            <CommunityChampions />
            <ListFirstProductBanner />
            <InviteNudgeBanner />

            <div className="bg-card border-b">
              <div className="container mx-auto px-4 py-3">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setSearchFocused(false)}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      // Enter = "show me everything": land on Listings (full
                      // results) and close the typeahead. Picking a specific
                      // suggestion in the dropdown is the direct path.
                      e.currentTarget.blur();
                      setSearchFocused(false);
                      if (searchActive) goToTabId("products");
                    }}
                    placeholder={searchPlaceholder}
                    className={searchActive ? "pl-9 pr-9" : "pl-9"}
                    data-testid="input-search"
                  />
                  {searchActive && (
                    <button
                      type="button"
                      aria-label="Clear search"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setSearchQuery("");
                        setSearchFocused(false);
                      }}
                      className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
                      data-testid="clear-search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  {showSearchResults && (
                    <BrowseSearchResults
                      query={searchQuery}
                      items={searchItems}
                      producers={searchProducers}
                      boxes={searchBoxes}
                      loading={searchLoading}
                      assist={assist}
                      assistLoading={assistLoading}
                      onPickItem={(id) => {
                        setSearchFocused(false);
                        if (searchLogIdRef.current) {
                          logSearchClick(searchLogIdRef.current, { itemId: id });
                        }
                        setLocation(`/items/${id}`);
                      }}
                      onPickProducer={(id) => {
                        setSearchFocused(false);
                        if (searchLogIdRef.current) {
                          logSearchClick(searchLogIdRef.current, { producerId: id });
                        }
                        setLocation(`/u/${id}`);
                      }}
                      onPickBoxes={() => {
                        setSearchFocused(false);
                        goToTabId("boxes");
                      }}
                      onSeeAll={(mode) => {
                        setSearchFocused(false);
                        goToTabId(mode);
                      }}
                      onRequest={() => {
                        // Land on Listings, where the request card lives with
                        // the full context of the empty result set.
                        setSearchFocused(false);
                        goToTabId("products");
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div ref={tablistRef}>
            <BrowseModeNav activeIndex={activeIndex} onSelect={setActiveIndex} />
          </div>
        </div>

        <SwipeTabs
          panels={panels}
          index={activeIndex}
          onIndexChange={setActiveIndex}
          panelHeight="100%"
          panelPaddingTop={overlayH}
          panelPaddingBottom={72}
          onActiveScroll={applyCollapse}
        />
      </div>

      <ShoppingCart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        items={cartItemsDetails}
        onUpdateQuantity={updateQuantity}
        onRemove={removeFromCart}
        onClearCart={clearCart}
        onCheckout={() => {
          setIsCartOpen(false);
          setLocation("/checkout");
        }}
      />
    </div>
  );
}
